// The skeleton outline of each module type — the one source for both the
// loading state (WidgetSkeleton.tsx) and the phone drag card (mobile/engine.ts),
// so they always match. Which type a module is lives in modules.json
// ("skeleton"; ModuleLoader.getSkeleton). Plain markup strings: static, no
// data, no behaviour. Styling: `.bm-skeleton` / `.skel-*` in styles.css.

export type SkeletonKind =
  | "table"
  | "bars"
  | "donuts"
  | "badges"
  | "line"
  | "columns"
  | "grid"
  | "radial";

const KINDS: readonly SkeletonKind[] = ["table", "bars", "donuts", "badges", "line", "columns", "grid", "radial"];

/** A module's skeleton type, from modules.json; "table" if unset / unknown. */
export function skeletonKindFor(title: string | null | undefined): SkeletonKind {
  const k = title ? window.ModuleLoader?.getSkeleton?.(title) : undefined;
  return KINDS.includes(k as SkeletonKind) ? (k as SkeletonKind) : "table";
}

const times = (n: number, html: (i: number) => string) => Array.from({ length: n }, (_, i) => html(i)).join("");

/** The inner markup of a skeleton of `kind` (goes inside `.bm-skeleton`). */
export function skeletonHTML(kind: SkeletonKind, rows?: number): string {
  switch (kind) {
    case "bars":
      return times(
        rows ?? 5,
        () => '<div class="skel-row"><div class="skel skel-label"></div><div class="skel skel-track"></div><div class="skel skel-value"></div></div>'
      );
    case "donuts":
      return '<div class="skel-donuts"><div class="skel skel-donut"></div><div class="skel skel-donut"></div></div>';
    case "badges":
      return times(
        rows ?? 2,
        () =>
          '<div class="skel-badges"><div class="skel skel-label"></div><div class="skel-badge-row">' +
          times(5, () => '<div class="skel skel-box"></div>') +
          "</div></div>"
      );
    case "line":
      return (
        '<div class="skel-chart"><svg class="skel-line-svg" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">' +
        '<path d="M0 30 C 12 22, 22 26, 34 18 S 56 20, 68 12 S 88 16, 100 8"/></svg></div>'
      );
    case "columns":
      return (
        '<div class="skel-chart skel-columns">' +
        [45, 70, 35, 85, 55, 65].map((h) => `<div class="skel skel-col" style="height:${h}%"></div>`).join("") +
        "</div>"
      );
    case "grid":
      return '<div class="skel-grid">' + times(25, () => '<div class="skel skel-cell"></div>') + "</div>";
    case "radial":
      return '<div class="skel-radial"><div class="skel-ring"></div><div class="skel-ring skel-ring-inner"></div></div>';
    default:
      return (
        '<div class="skel skel-head"></div>' +
        times(rows ?? 4, () => '<div class="skel-row"><div class="skel skel-team"></div><div class="skel skel-value"></div></div>')
      );
  }
}
